import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AudioOutlined, RobotOutlined, UnorderedListOutlined, SettingOutlined } from '@ant-design/icons';
import { fetchEvent } from 'api/events';
import { fetchConfig } from 'api/config';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { Menu, Spin, App } from "antd";
import EventCard from './EventCard';
import Page from 'components/Page';
import { useRecording } from '@newtontechnologies/libs-browser-mic/recording';
import { EventContextReact } from './event-context';
import SegmentRecordSubpage from './subpages/SegmentRecordSubpage';
import SegmentsSubpage from './subpages/SegmentsSubpage';
import SettingsSubpage from './subpages/SettingsSubpage';
import AgentSubpage from './subpages/AgentSubpage';
import { buildUrlPath } from 'lib/resolveUrl';
import { getCurrentServerTime } from 'lib/server-clock';
import { EventStateProvider, useEventAdminState } from 'components/EventStateProvider';
import { useI18n } from 'components/I18nProvider';
import { useWsAudioUpload } from 'lib/use-ws-audio-upload';
import { useFinishSegment } from 'lib/use-finish-segment';
import { fetchTestRecordings } from 'api/testing';
import styles from './style.module.scss';
const EventPageInner = ({ eventId }) => {
    const navigate = useNavigate();
    const { intl } = useI18n();
    const [recording, recordingControl] = useRecording({
        getCurrentServerTime,
        sampleRate: 16000,
        chunkSizeInSamples: 4096,
    });
    const { eventSnapshot, selectedSegmentId, wsClient, connectionId, transportOnline, } = useEventAdminState();
    const { finishSegment } = useFinishSegment(eventId);
    const [testRecordings, setTestRecordings] = useState([]);
    useEffect(() => {
        void fetchTestRecordings().then((result) => {
            if (result.isSuccess()) {
                setTestRecordings(result.get());
            }
        });
    }, []);
    const handleTestRecordingEnded = useCallback(async () => {
        if (selectedSegmentId === null) {
            return;
        }
        recordingControl.stop();
        await finishSegment(selectedSegmentId);
    }, [selectedSegmentId, recordingControl, finishSegment]);
    useWsAudioUpload({
        eventSnapshot,
        connectionId,
        wsClient,
        transportOnline,
        recordingControl,
        recordingPhase: recording.phase,
        testRecordings,
        onTestRecordingEnded: () => {
            void handleTestRecordingEnded();
        },
    });
    const [agentEnabled, setAgentEnabled] = useState(false);
    const { params: { subpage } } = useMatch(buildUrlPath('/admin/events/:eventId/:subpage?'));
    const eventBasePath = buildUrlPath(`/admin/events/${eventId}`);
    useEffect(() => {
        fetchConfig().then(c => setAgentEnabled(!!c.agentEnabled)).catch(() => setAgentEnabled(false));
    }, []);
    const menuItems = useMemo(() => {
        const items = [
            {
                label: _jsx("span", { "test-id": "admin-event-tab-record", children: intl.formatMessage({ id: 'eventTabRecord' }) }),
                key: 'record',
                icon: _jsx(AudioOutlined, {}),
            },
            {
                label: _jsx("span", { "test-id": "admin-event-tab-segments", children: intl.formatMessage({ id: 'segments' }) }),
                key: 'segments',
                icon: _jsx(UnorderedListOutlined, {}),
            },
            {
                label: _jsx("span", { "test-id": "admin-event-tab-settings", children: intl.formatMessage({ id: 'settings' }) }),
                key: 'settings',
                icon: _jsx(SettingOutlined, {}),
            },
        ];
        if (agentEnabled) {
            items.push({
                label: _jsx("span", { "test-id": "admin-event-tab-agent", children: intl.formatMessage({ id: 'eventTabAgent' }) }),
                key: 'agent',
                icon: _jsx(RobotOutlined, {}),
            });
        }
        return items;
    }, [intl, agentEnabled]);
    useEffect(() => {
        return () => {
            recordingControl.stop();
            recordingControl.abandonFeedbackMicrophone();
        };
    }, []);
    useEffect(() => {
        if (recording.phase === 'running') {
            const preventUnload = (e) => {
                e.preventDefault();
            };
            window.addEventListener('beforeunload', preventUnload);
            return () => {
                window.removeEventListener('beforeunload', preventUnload);
            };
        }
    }, [recording.phase]);
    const handleMenuItemClick = (info) => {
        if (info.key === 'record') {
            navigate(eventBasePath);
        }
        else if (info.key === 'segments') {
            navigate(`${eventBasePath}/segments`);
        }
        else if (info.key === 'settings') {
            navigate(`${eventBasePath}/settings`);
        }
        else if (info.key === 'agent') {
            navigate(`${eventBasePath}/agent`);
        }
    };
    const tab = subpage ?? 'record';
    const eventContextValue = { recording, recordingControl };
    return (_jsxs("div", { className: styles.eventPageSection, children: [_jsx(EventCard, { access: "ok" }), _jsxs("div", { children: [_jsx(Menu, { className: styles.menu, mode: "horizontal", items: menuItems, onClick: handleMenuItemClick, selectedKeys: [tab], "test-id": "admin-event-tab-menu" }), _jsxs(EventContextReact.Provider, { value: eventContextValue, children: [_jsx("div", { hidden: tab !== 'record', "aria-hidden": tab !== 'record', children: _jsx(SegmentRecordSubpage, { eventId: eventId }) }), _jsx("div", { hidden: tab !== 'segments', "aria-hidden": tab !== 'segments', children: _jsx(SegmentsSubpage, {}) }), _jsx("div", { hidden: tab !== 'settings', "aria-hidden": tab !== 'settings', children: _jsx(SettingsSubpage, {}) }), agentEnabled && (_jsx("div", { hidden: tab !== 'agent', "aria-hidden": tab !== 'agent', children: _jsx(AgentSubpage, { eventId: eventId }) }))] })] })] }));
};
const EventPage = () => {
    const { eventId } = useParams();
    const { intl } = useI18n();
    const { message } = App.useApp();
    const [eventAccess, setEventAccess] = useState('loading');
    useEffect(() => {
        const load = async () => {
            if (eventId === undefined) {
                return;
            }
            setEventAccess('loading');
            try {
                const result = await fetchEvent(eventId);
                setEventAccess(result === 'not-found' ? 'not-found' : 'ok');
            }
            catch (error) {
                console.error('Failed to fetch event:', error);
                void message.error(intl.formatMessage({ id: 'eventLoadFailed' }));
                setEventAccess('error');
            }
        };
        void load();
    }, [eventId, intl, message]);
    if (eventId === undefined) {
        return (_jsx(Page, { children: _jsx(Spin, {}) }));
    }
    if (eventAccess === 'loading') {
        return (_jsx(Page, { children: _jsx(Spin, {}) }));
    }
    if (eventAccess === 'not-found') {
        return (_jsx(Page, { children: _jsx(EventStateProvider, { eventId: eventId, children: _jsx(EventCard, { access: "not-found" }) }) }));
    }
    if (eventAccess === 'error') {
        return (_jsx(Page, { children: _jsx(EventStateProvider, { eventId: eventId, children: _jsx(EventCard, { access: "error" }) }) }));
    }
    return (_jsx(Page, { children: _jsx(EventStateProvider, { eventId: eventId, children: _jsx(EventPageInner, { eventId: eventId }) }) }));
};
export default EventPage;
